import { IPresenterOwner, Presenter } from 'wdc-cube'

import { PriceTierEditorScope } from './bl_scopes'
import type { PriceTierField, PriceTierInput } from './bl_types'

export const DEFAULT_TIERS: PriceTierInput[] = [
    { minQty: 1, maxQty: 50, unitPrice: 1 },
    { minQty: 51, maxQty: 100, unitPrice: 0.75 },
    { minQty: 101, maxQty: null, unitPrice: 0.5 }
]

export type PriceTierEditorOptions = {
    readOnly?: boolean
    onChanged?: () => void
}

export class PriceTierEditorPresenter extends Presenter<PriceTierEditorScope, IPresenterOwner> {
    public constructor(owner: IPresenterOwner, scope: PriceTierEditorScope, options?: PriceTierEditorOptions) {
        super(owner, scope)

        this.#onChanged = options?.onChanged

        scope.readOnly = !!options?.readOnly
        scope.onTierChanged = this.action(this.handleTierChanged)
        scope.onTierAdded = this.action(this.handleTierAdded)
        scope.onTierRemoved = this.action(this.handleTierRemoved)
    }

    readonly #onChanged?: () => void

    setTiers(tiers: PriceTierInput[]) {
        this.scope.tiers = tiers
        this.scope.canRemoveTier = !this.scope.readOnly && tiers.length > 1
        this.scope.errors = this.scope.readOnly ? [] : validateTiers(tiers)
    }

    validate(): string[] {
        const errors = this.scope.readOnly ? [] : validateTiers(this.scope.tiers)
        this.scope.errors = errors
        return errors
    }

    clearErrors() {
        this.scope.errors = []
    }

    private async handleTierChanged(index: number, field: PriceTierField, value: string) {
        if (this.scope.readOnly || !this.scope.tiers[index]) {
            return
        }

        this.#apply(this.scope.tiers.map((tier, i) => (i === index ? { ...tier, ...patchOf(field, value) } : tier)))
    }

    private async handleTierAdded() {
        if (this.scope.readOnly) {
            return
        }

        const tiers = this.scope.tiers
        const last = tiers[tiers.length - 1]
        const minQty = last ? Math.max(last.minQty + 1, (last.maxQty ?? last.minQty) + 1) : 1

        const next = [...tiers]

        if (last && (last.maxQty === null || last.maxQty === undefined)) {
            next[next.length - 1] = { ...last, maxQty: minQty - 1 }
        }

        next.push({ minQty, maxQty: null, unitPrice: 0 })
        this.#apply(next)
    }

    private async handleTierRemoved(index: number) {
        if (this.scope.readOnly) {
            return
        }

        const next = this.scope.tiers.filter((_, i) => i !== index)

        if (next.length > 0) {
            next[next.length - 1] = { ...next[next.length - 1], maxQty: null }
        }

        this.#apply(next)
    }

    #apply(tiers: PriceTierInput[]) {
        this.setTiers(tiers)
        this.#onChanged?.()
    }
}

const MAX_PRICE_DECIMALS = 6

function validateTiers(tiers: PriceTierInput[] | undefined | null): string[] {
    const errors: string[] = []

    if (!tiers || tiers.length === 0) {
        return ['Informe ao menos uma faixa de preço.']
    }

    const sorted = [...tiers].sort((a, b) => a.minQty - b.minQty)

    for (let i = 0; i < sorted.length; i++) {
        const tier = sorted[i]
        const pos = i + 1

        if (!Number.isInteger(tier.minQty) || tier.minQty < 1) {
            errors.push(`Faixa ${pos}: quantidade inicial deve ser um inteiro maior ou igual a 1.`)
        }

        if (tier.maxQty !== null && tier.maxQty !== undefined) {
            if (!Number.isInteger(tier.maxQty)) {
                errors.push(`Faixa ${pos}: quantidade final deve ser um número inteiro.`)
            } else if (tier.maxQty < tier.minQty) {
                errors.push(`Faixa ${pos}: quantidade final menor que a inicial.`)
            }
        }

        if (!Number.isFinite(tier.unitPrice) || tier.unitPrice < 0) {
            errors.push(`Faixa ${pos}: preço unitário inválido.`)
        } else if (decimalPlaces(tier.unitPrice) > MAX_PRICE_DECIMALS) {
            errors.push(`Faixa ${pos}: preço unitário com mais de ${MAX_PRICE_DECIMALS} casas decimais.`)
        }
    }

    if (sorted[0].minQty !== 1) {
        errors.push('A primeira faixa deve começar em 1.')
    }

    for (let i = 1; i < sorted.length; i++) {
        const previous = sorted[i - 1]
        const current = sorted[i]

        if (previous.maxQty === null || previous.maxQty === undefined) {
            errors.push(`Somente a última faixa pode ficar sem limite superior (faixa ${i} está aberta).`)
            continue
        }

        if (current.minQty !== previous.maxQty + 1) {
            errors.push(`As faixas devem ser contínuas: a faixa ${i + 1} deveria começar em ${previous.maxQty + 1}.`)
        }
    }

    const last = sorted[sorted.length - 1]
    if (last.maxQty !== null && last.maxQty !== undefined) {
        errors.push('A última faixa deve ser aberta (sem limite superior).')
    }

    return errors
}

function decimalPlaces(value: number): number {
    const text = String(value)
    const dot = text.indexOf('.')
    if (dot < 0) {
        return text.includes('e-') ? Number(text.split('e-')[1]) : 0
    }
    return text.length - dot - 1
}

function patchOf(field: PriceTierField, value: string): Partial<PriceTierInput> {
    switch (field) {
        case 'minQty':
            return { minQty: toInt(value) ?? 0 }
        case 'maxQty':
            return { maxQty: toInt(value) }
        default:
            return { unitPrice: toFloat(value) ?? 0 }
    }
}

function toInt(value: string): number | null {
    if (!value) {
        return null
    }
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
}

function toFloat(value: string): number | null {
    if (!value) {
        return null
    }
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
}

export default PriceTierEditorPresenter
